/**
 * Hero Component — Case Associates LP override
 *
 * Full-width hero section with background image, property name,
 * subtitle line, and embedded contact form.
 * Features: Ken Burns background, staggered entrance animations,
 * bottom gradient fade.
 *
 * Overrides the base theme Hero to consolidate the subtitle into a single
 * rich-text token (hero.subtitle_line) that embeds field references for
 * bedroom range, city, and state.
 */

import {
  useContentToken,
  EditableToken,
  useSlotGroup,
  EditableSlotGroup,
  useSlotGroupOverrides,
  useBlockSlot,
  SectionContextProvider,
  RichTextContent,
} from '@vibesites/runtime/react';
import type { SlotGroupDefinition, ComponentContentSchema } from '@vibesites/runtime';

const heroImageDefinition: SlotGroupDefinition = {
  label: 'Hero Background',
  fields: {
    image: {
      label: 'Background Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property exterior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: {
      image: 'image',
      alt: 'alt_text',
    },
  },
};

export function Hero() {
  const slotGroupOverrides = useSlotGroupOverrides();

  const propertyName = useContentToken('hero.property_name');

  const formSlot = useBlockSlot('hero.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  const heroImage = useSlotGroup('hero.background', heroImageDefinition);

  return (
    <section className="relative min-h-[90vh] lg:min-h-[90vh] md:min-h-auto bg-primary flex items-center overflow-hidden">
      {/* Background image with Ken Burns effect */}
      {heroImage.image && (
        <div
          className="absolute inset-0 bg-cover bg-center animate-[kenBurns_25s_ease-in-out_infinite_alternate]"
          style={{ backgroundImage: `url(${heroImage.image})` }}
        />
      )}

      {/* Background overlay */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Bottom gradient fade — uses neutral black to avoid theme color bleed */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/30 to-transparent z-[2]" />

      <div className="relative z-[3] w-full max-w-[1400px] mx-auto px-4 py-8 md:px-6 md:py-12 lg:px-6 lg:py-16 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
        {/* Left content */}
        <div className="text-white">
          <EditableSlotGroup
            groupPath="hero.background"
            definition={heroImageDefinition}
            override={slotGroupOverrides['hero.background']}
            style={{ position: 'absolute', top: '1rem', left: '1rem', zIndex: 10 }}
          >
            <span className="hidden">Edit Background Image</span>
          </EditableSlotGroup>

          <div className="animate-[fadeInUp_0.8s_ease-out_both]">
            <EditableToken path="hero.property_name" type="text" label="Property Name" as="div">
              <h1 className="text-[clamp(2rem,5vw,4rem)] font-bold font-heading mb-4 [text-shadow:0_2px_10px_rgba(0,0,0,0.3)] leading-[1.1]">
                {propertyName}
              </h1>
            </EditableToken>
          </div>

          <div className="animate-[fadeInUp_0.8s_ease-out_0.2s_both]">
            <RichTextContent
              path="hero.subtitle_line"
              as="h2"
              className="text-[clamp(1rem,2.5vw,1.75rem)] font-normal font-body [text-shadow:0_1px_5px_rgba(0,0,0,0.3)] leading-relaxed"
            />
          </div>
        </div>

        {/* Form area */}
        <div className="flex justify-end lg:justify-end justify-center animate-[fadeInUp_0.8s_ease-out_0.4s_both] scroll-mt-20" id="contact">
          <EditableToken
            path="hero.form_block"
            type="block"
            label="Form Block"
            as="div"
            style={{ width: '100%', maxWidth: '450px' }}
          >
            {formSlot ? (
              <div className="backdrop-blur-sm bg-white/5 rounded-2xl p-1.5">
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              </div>
            ) : (
              <div className="bg-white/10 rounded-lg p-12 px-8 text-center text-white/60 border-2 border-dashed border-white/20">
                <p>No form block selected</p>
                <p className="text-sm opacity-70">
                  Select a block in the editor sidebar
                </p>
              </div>
            )}
          </EditableToken>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes kenBurns {
          from { transform: scale(1); }
          to { transform: scale(1.08); }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero',
  tokens: {
    'hero.property_name': {
      label: 'Property Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Welcome Home',
    },
    'hero.subtitle_line': {
      label: 'Hero Subtitle',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_bedrooms_range"></span> Bedroom Apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'hero.form_block': {
      label: 'Form Block',
      type: 'block',
      description: 'The block component to render in the hero form area',
      defaultValue: 'multihub-lead-form',
    },
  },
  slotGroups: {
    'hero.background': heroImageDefinition,
  },
};

(Hero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Hero;
